import React, { useCallback, useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, IconButton, MenuItem, Select, SelectChangeEvent } from "@mui/material";
import AddIcon from '@mui/icons-material/Add';
import EditNoteIcon from '@mui/icons-material/EditNote';
import Box from '@mui/material/Box';
import MuiTextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import { FormikProps, useFormik } from 'formik';
import { Grid2 as Grid } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { useNavigate } from "react-router-dom";
import * as Yup from 'yup';

import { ILayoutContext, LayoutContext, DataLoader, useUderlyStore, CellMediaThumb } from "@uderly/react-uderly-ui";
import { IndexedRow } from "@uderly/react-uderly-ui/models";
import { createItemRequest, getRecordsRequest } from '@uderly/react-uderly-ui/api';
import Settings from '../settings';
import { GridColDef } from "@mui/x-data-grid";
import AppContext from '../AppContext';
import QuickModule from "../../uderly/components/QuickModule";

export interface PlaylistsSelectProps {
  exclude?: IndexedRow;
  compactMode?: boolean; // Display it as a dropdown
  playlistId?: number;
  onSelect?: (item: any) => void;
  quickCreatePlaylist?: () => void; // TODO: Move in this file
  onPlaylistChanged?: (e: any, playlistId: number) => void; // TODO: Move in this file
}

const QuickCreatePlaylistDialog = ({ onChanged }: { onChanged(v: string): void }) => {
  const { t } = useTranslation("translation");

  const validationSchema = Yup.object({
    playlistName: Yup.string().min(2).required((t('required-field') as string))
  });

  const formik = useFormik({
    initialValues: {
      playlistName: ''
    },
    validationSchema: validationSchema,
    onSubmit: (values) => {
      // alert(JSON.stringify(values, null, 2));
      onChanged(values.playlistName);
    },
  });

  return (
    <form className="insert-playlist-form" onSubmit={formik.handleSubmit}>
      <MuiTextField
        fullWidth
        name="playlistName"
        label={t('name')}
        value={formik.values.playlistName}
        onChange={formik.handleChange}
        onBlur={formik.handleBlur}
        error={formik.touched.playlistName && Boolean(formik.errors.playlistName)}
        helperText={formik.touched.playlistName && formik.errors.playlistName} />

      <Button color="primary" variant="contained" fullWidth type="submit">
        {t('create')}
      </Button>
    </form>
  );
}

const PlaylistsSelect = (props: PlaylistsSelectProps) => {
  const layoutContext = useContext(LayoutContext) as ILayoutContext;

  const store = useUderlyStore.getState().store;

  const navigate = useNavigate();

  const { exclude, compactMode, onSelect, quickCreatePlaylist, onPlaylistChanged } = props;
  const { t } = useTranslation("translation");

  const [playlists, setPlaylists] = useState<[] | null>(null);
  const [playlistId, setPlaylistId] = useState<number | undefined>(props.playlistId);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showInsertPlaylist, setShowInsertPlaylist] = useState<boolean>(false);
  const [newPlaylistName, setNewPlaylistName] = useState<string>("");

  const apiPath = "/playlists";

  const columns: GridColDef[] = [{
    field: 'name',
    headerName: t('name') as string,
    minWidth: 100,
    flex: 1,
    renderCell: params => {
      const { row } = params;

      return (
        <div className="td-v-center">
          <div className="row-thumb">
            {(row.media && row.media[0]) && <>
              <img src={((row.media[0].disk === "net") ? Settings.apiUrl : AppContext.s["api-url"]) + apiPath + "/" + row.id + "/image/thumb"} alt="Thumb" />
            </>}
            {(!row.media || !row.media[0]) && <img src={AppContext.s["placeholder"]} className="placeholder-thumb" alt="Thumb" />}
          </div>

          {row.name}
        </div>
      );
    }
  },
  {
    field: 'questions-count',
    headerName: "",
    minWidth: 40,
    renderCell: params => {
      const { row } = params;

      return <div className="td-v-center">{`${row.questions_count} ${t('questions')}`}</div>;
    }
  }];

  const fetchRecordsAsync = async (newFilters: any = {}, selectedPlaylistId?: number) => {
    const _defaultFilters = {
      page: 1,
      page_size: 25,
    };

    const updatedFilters = Object.assign(
      { resolve_questions: true },
      _defaultFilters,
      newFilters
    );

    const url = Settings.apiUrl + "/playlists";
    const response = await getRecordsRequest(
      url,
      updatedFilters,
      // fetchRecordsAbortController.current
    );

    if (response && response.status === 200) {
      const { data } = response.data;

      setPlaylists(exclude ? data.filter(x => x.id !== exclude.id) : data);

      if (selectedPlaylistId)
        setTimeout(() => {
          setPlaylistId(selectedPlaylistId);
        }, 10);
    }
  };

  const fetchRecords = useCallback(fetchRecordsAsync, [exclude]);

  useEffect(() => {
    fetchRecords();
  }, [exclude, fetchRecords]);

  const onInsertPlaylist = () => {
    setShowInsertPlaylist(true);
  }

  const onInsertToNewPlaylist = async () => {
    const response = await createItemRequest(Settings.apiUrl + "/playlists", {
      name: newPlaylistName
    });

    if (response && response.status > 200 && onSelect)
      onSelect(response.data.data);
  }

  const onSelected = (o) => {
    setIsLoading(true);

    if (onSelect)
      onSelect(o);

    setIsLoading(false);
  }

  const topOptions = (<>
    {!showInsertPlaylist ?
      <Button onClick={onInsertPlaylist}>{AppContext.r["new-playlist"]}</Button>
      : <div className="quick-new-playlist">
        <MuiTextField variant="outlined" label={t("name")}
          value={newPlaylistName} onChange={(event) => setNewPlaylistName(event.target.value)} />

        <Box marginLeft={1}>
          <Button
            disabled={newPlaylistName.length === 0}
            onClick={onInsertToNewPlaylist}>{t("insert")}</Button>
        </Box>

        <Box marginLeft={1}>
          <Button onClick={() => setShowInsertPlaylist(false)}>{t("cancel")}</Button>
        </Box>
      </div>}
  </>);

  const filtersForm = (formik: FormikProps<any>) => (
    <form onChange={formik?.handleChange}>
      <Grid container spacing={1}>
        <Grid size={{ xs: 12 }}>
          <MuiTextField
            fullWidth
            id="search"
            name="search"
            label={t('search')}
            value={formik.values.email}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.email && Boolean(formik.errors.email)}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              },
            }}
          />
        </Grid>
      </Grid>
    </form>
  );

  const onFiltersChanged = async (values: any) => {
    fetchRecords(values);
  };

  const internalQuickCreatePlaylist = () => {
    layoutContext.dialog.show({
      title: t('new-playlist'),
      content: <QuickCreatePlaylistDialog onChanged={async v => {
        const response = await createItemRequest(`${Settings.apiUrl}/playlists`, { name: v });

        if (response && response.status === 201) {
          const playlist = (response.data.data);

          console.log(playlist)

          await fetchRecordsAsync({}, playlist.id);

          if (onPlaylistChanged)
            onPlaylistChanged(null, playlist.id);
        }

        layoutContext.dialog.hide();
      }} />,
    })
  }

  const handleChange = (event: SelectChangeEvent<number>) => {
    const {
      target: { value },
    } = event;

    setPlaylistId(value as number);

    if (onPlaylistChanged)
      onPlaylistChanged(event, value as number);
  };

  return <div className={`playlists-select ${compactMode ? 'compact' : ''}`}>
    {(!playlists || isLoading) && <DataLoader />}

    {compactMode && playlists && ((playlists.length > 0) ? <>
      <Select
        size="small"
        value={playlistId}
        onChange={handleChange}>
        {playlists.map((p: any) => <MenuItem value={p.id}><CellMediaThumb o={p} path={"/playlists"} /> {p.name}</MenuItem>)}
      </Select>

      {playlistId &&
        <IconButton aria-label="edit" onClick={() => navigate(`/${store?.subdomain}/playlists/${playlistId}`)}>
          <EditNoteIcon />
        </IconButton>}

      <IconButton aria-label="add" onClick={internalQuickCreatePlaylist}>
        <AddIcon />
      </IconButton>
    </> : <div className="no-playlists">
      <p>{t("no-playlists")}</p>
      <Button className="link-button" onClick={quickCreatePlaylist}>{t("create-playlist")}</Button>
    </div>)}

    {!compactMode && playlists &&
      <QuickModule
        columns={columns}
        records={playlists}
        onSelected={onSelected}
        initialFilters={{ search: '' }}
        filtersForm={filtersForm}
        topOptions={topOptions}
        onFiltersChanged={onFiltersChanged}
        disableView />}
  </div>;
}

export default PlaylistsSelect;